import { isScope } from '../../components/Scope';
import { isPropertyObserver, observeArray, observeProperties } from '../../Observer';
import { Observable, Signal, Subscription } from '../../Signal';
import { compile } from '../Compiler';
import { context, Template, TemplateContext } from '../Template';
import { create, invokeInit, View } from '../View';
import { ClosureExpression, NodeEventExpression } from './ClosureExpression';
import { Expression, hasInital, snakeToCamel } from './Expression';
import { AttributeExpression, RenderExpression } from './RenderExpression';


/**
 * This class monitors expression on the node view attribute like: <div view="[[ my expression ]]"></div>
 */
export class ViewExpression extends AttributeExpression {

	expressions: Expression[];
	template: Template;
	view: View;
	mergers: Array<AttributeMerger>;
	rerenderCheck: RerenderCheck;
	innerTemplates: Array<{ name:string, template:string, data:string }>;

	constructor( node: Element, template: Template ) {
		super( node, node.attributes.getNamedItem( 'view' ), template.context );
		this.template = template;
		this.expressions = [];
		this.mergers = [];

		this.parseAttributes();
		this.parseInnerTemplates();

	}

	inital() {
		this.renderView();
	}


	update() {
		// flag rerendering
		this.rerenderCheck.flag = true;
	}

	renderView() {

		// restore original attribute values from mergers
		for( const merger of this.mergers ) {
			merger.reset();
		}
		// delete old mergers
		this.mergers = [];

		// destroy old view if there is any
		if( this.view ) this.view.destroy();

		let viewName = this.getExpressionValue();

		// create view baed on its context
		this.view = create( viewName, this.context );

		// stop rendering if there is no valid view
		if( !this.view ) return;


		// inital execute render expression so the view data is correctly set
		this.expressions.forEach( expr => {
			if( hasInital( expr ) ) expr.inital();
		} );

		// check for scope view that need the inner content of this node
		if( isScope( this.view ) ) this.view.prepareContent( this.node, this.template );

		// register inner templates
		for (const template of this.innerTemplates ) {
			this.view.registry.add( template.name, () => {
				// if there is a template expression for the data invoke it with this context.
				const data = ( template.data ) ? compile( template.data, [ 'data' ] ).call( this.context, this.context.data ) : this.context.data;
				return new View( { template:template.template, data } );
			})
		}

		// render the view to merge it afterwards
		this.view.template.render();

		// properly set the view attribute based on the registred key
		this.node.setAttribute( 'view', this.getViewName( viewName ) );

		// define what attributes should merge from outside/inside
		this.mergeNodes();


		// subscribe on data events after render
		this.expressions.forEach( expr => {
			if( expr instanceof DataEventExpression ) expr.updateSubscription();
		} );

		// make sure rerender check is last
		this.rerenderCheck.subscribe();

		// call init after node is correctly set
		invokeInit( this.view );
	}




	/**
	 * Parses node attributes of this view node
	 */
	parseAttributes() {

		let attrs = Array.prototype.slice.call( this.node.attributes );

		for( const attr of attrs ) {

			// check on data expression
			if( attr.name == 'data' ) {
				let dataExpr = new DataExpression( this, attr.value );
				this.rerenderCheck = new RerenderCheck( this, attr.value + ' ' + this.node.getAttribute( 'view' ) );
				this.node.removeAttribute( attr.name );
				this.expressions.unshift( dataExpr ); // make sure data is set first
			} else {
				this.rerenderCheck = new RerenderCheck( this, this.node.getAttribute( 'view' ) );
			}

			// check on data attribite expression
			if( attr.name.indexOf( 'data-' ) == 0 ) {
				let expr = new DataAttributeExpression( this, attr.value, snakeToCamel( attr.name.substring( 5 ) ) );
				this.node.removeAttribute( attr.name );
				this.expressions.push( expr );
			}

			// handle propertyChanged events
			if( attr.name.indexOf( 'on-data-' ) == 0 ) {
				let event = snakeToCamel( attr.name.replace( 'on-data-', '' ) );
				let eventExpression = new AttributeEventExpression( this, attr.value, event );
				this.node.removeAttribute( attr.name );
				this.expressions.push( eventExpression );
			}

			// handle custom signal events
			if( attr.name.indexOf( 'on-' ) == 0 ) {
				let event = snakeToCamel( attr.name.replace( 'on-', '' ) );
				let expr = new SignalEventExpression( this, attr.value, event );
				this.node.removeAttribute( attr.name );
				this.expressions.push( expr );
			}

		}
	}


	parseInnerTemplates( ) {
		// parse all <template name="..."> nodes
		const templateNodes = Array.from( this.node.children ).filter( n => n.tagName == 'TEMPLATE' );
		this.innerTemplates = Array.from( templateNodes ).map( node => {
			node.parentNode.removeChild( node );
			return {
				name: node.getAttribute( 'name' ),
				template: node.innerHTML,
				data: node.getAttribute( 'data' )
			}
		});

		// if there is an inner content auto register as 'Content?
		if( this.node.innerHTML.trim() != '' ) {
			this.innerTemplates.push( {
				name: 'Content',
				template: this.node.innerHTML,
				data: undefined
			})
		}
	}


	destroy() {
		if( this.view ) this.view.destroy();
		for( const exp of this.expressions ) {
			exp.destroy();
		}
		this.rerenderCheck.destroy();
	}



	/**
	 * Merges inner view node with the other view node.
	 */
	mergeNodes() {

		// get all attributes from outside and inside and make them unique
		let attributes = Array.prototype.slice.call( this.node.attributes ).concat( Array.prototype.slice.call( this.view.node.attributes ) ).map( attr => attr.name );
		attributes = attributes.filter( ( item, i ) => attributes.indexOf( item ) === i );

		// merge each attribute
		attributes.forEach( attr => {

			let merger = new AttributeMerger( this.node, attr );
			merger.setupInner( this.view.template );
			merger.setupOuter( this.template );
			merger.update();

			this.mergers.push( merger );
		} );

		// empty target node
		this.node.innerHTML = '';

		// copy children
		while( this.view.node.hasChildNodes() ) {
			this.node.appendChild( this.view.node.firstChild );
		}


		// adjust event expressions to new node
		let eventExpressions = this.view.template.filterExpressions( expr => expr instanceof NodeEventExpression && expr.node == this.view.node ) as NodeEventExpression[];
		eventExpressions.forEach( expr => {
			expr.node = this.node;
			expr.updateSubscription(); // will reregister on new target
		} );

		// properly setup node refenrence
		this.view.template.node = this.node;

	}


	getViewName( key: any ) {
		if( typeof key == 'string' ) return key;
		if( typeof key == 'function' ) return key.toString().match( /function\s(.*?)\(/ )[ 1 ];
		if( typeof key == 'object' && key !== null ) {
			// match es6 class
			let m = key.constructor.toString().match( /class\s(.*?)\s/ );
			if( m ) return m[ 1 ];
			// match es5 class
			m = key.constructor.toString().match( /function\s(.*?)\(/ );
			if( m ) return m[ 1 ];
		}
		return '';
	}

}


/**
 * This class monitors expression on data attributes like: <div view=".." data-name="[[ my expression ]]"></div>
 */
class DataAttributeExpression extends RenderExpression {

	attribute: string;
	viewExpression: ViewExpression;

	constructor( viewExpression: ViewExpression, expression: string, attribute: string ) {
		super( viewExpression.context, expression );
		this.attribute = attribute;
		this.viewExpression = viewExpression;
	}

	update() {
		this.viewExpression.view.data[ this.attribute ] = this.getExpressionValue();
	}
}

/**
 * This class monitors expression on data the attributes like: <div view=".." data="[[ my expression ]]"></div>
 */
class DataExpression extends RenderExpression {

	viewExpression: ViewExpression;

	constructor( viewExpression: ViewExpression, templateString: string ) {
		super( viewExpression.context, templateString );
		this.viewExpression = viewExpression;
	}

	inital() {
		const d = this.getExpressionValue();
		// only set data if there is a valid data
		if( d != undefined ) this.viewExpression.view.data = d;
	}

	update() {
		// flag rerendering
		this.viewExpression.rerenderCheck.flag = true;
	}
}




/**
 * Super
 */
class DataEventExpression extends ClosureExpression {

	viewExpression: ViewExpression;

	constructor( viewExpression: ViewExpression, expression: string ) {
		super( viewExpression.context, expression );
		this.viewExpression = viewExpression;
	}

	updateSubscription() {

	}
}
/**
 * This class monitors expression of data events like: <div view=".." on-data-attribute="[[ demo = event.value ]]"></div>
 */
class AttributeEventExpression extends DataEventExpression {

	attribute: string;
	arraySubscription:Subscription;

	constructor( viewExpression: ViewExpression, expression: string, attribute: string ) {
		super( viewExpression, expression );
		this.attribute = attribute;
	}

	updateSubscription() {

		let data = this.viewExpression.view.data as any;
		if (!data) { return; }

		// register on view data
		let d = observeProperties( data, this.attribute );
		this.subscribe( d.propertyChange.filter( e => e.name == this.attribute ) );

		// register on array changes if its an array attribute
		let value = data[ this.attribute ];
		if( Array.isArray( value ) ) this.subscribeArray( observeArray( value ).arrayChange );

		// inital invoke to adjust values outside
		this.invoke();
	}

	invoke() {
		super.invoke( { name: this.attribute, value: this.viewExpression.view.data[ this.attribute ] } );
	}

	subscribeArray( observable:Observable<any> ) {
		this.unsubscribeArray();
		this.arraySubscription = observable.subscribe( this.invoke.bind( this ) );
	}

	unsubscribeArray() {
		if( this.arraySubscription ) this.subscription.unsubscribe();
	}

	destroy() {
		super.destroy();
		this.unsubscribeArray();
	}
}

/**
 * This class subscribes model signals and invokes the corresponding closure like: <div on-my-signal="[[ ... ]]"></div>
 */
class SignalEventExpression extends DataEventExpression {

	signalName: string;

	constructor( viewExpression: ViewExpression, expression: string, signalName: string ) {
		super( viewExpression, expression );
		this.signalName = signalName;
	}

	updateSubscription() {

		const data = this.viewExpression.view.data;
		if (!data) { return; }
		// check for valid signal and subscribe
		let signal = data[ this.signalName ];

		if( signal instanceof Signal ) {
			this.subscribe( signal );
		}
	}
}


class AttributeMerger {

	node: Element;
	attr: string;
	inner: AttributeExpression;
	outer: AttributeExpression;

	constructor( node: Element, attr: string ) {
		this.node = node;
		this.attr = attr;
	}

	setupInner( template: Template ) {
		this.inner = template.filterExpressions( expr => expr instanceof AttributeExpression && expr.node == template.node && expr.attribute.name == this.attr )[ 0 ] as AttributeExpression;
		if( this.inner && !( this.inner instanceof ViewExpression ) ) {
			// use merge function to update
			this.inner.update = this.update.bind( this );
		} else {
			// create dummy expression for later use
			this.inner = new AttributeExpression( template.node, this.getAttrOf( template.node ), template.context );
		}
	}

	setupOuter( template: Template ) {
		this.outer = template.filterExpressions( expr => expr instanceof AttributeExpression && expr.node == this.node && expr.attribute.name == this.attr )[ 0 ] as AttributeExpression;
		if( this.outer && !( this.outer instanceof ViewExpression ) ) {
			this.outer.update = this.update.bind( this );
		} else {
			this.outer = new AttributeExpression( this.node, this.getAttrOf( this.node ), template.context );
		}
	}


	update() {

		let outerContent = this.outer.getExpressionValue();
		let innerContent = this.inner.getExpressionValue();

		// handle class attribute to join inner & outer classes
		if( this.attr == 'class' ) {

			let classes = ( outerContent + ' ' + innerContent ).split( ' ' ).filter( c => c.length > 0 );
			let list = this.node.classList;

			this.node.setAttribute( 'class', '' ); // reset
			list.add.apply( list, classes );

			// handle all regular attributes where inner wins
		} else {
			this.node.setAttribute( this.attr, innerContent ? innerContent : outerContent );
		}
	}

	getAttrOf( node: Element ) {
		let attr = node.attributes[ this.attr ];
		if( !attr ) attr = { name: this.attr, value: '' };
		return attr;
	}

	reset() {
		this.node.setAttribute( this.attr, this.outer.getExpressionValue() );
	}

}


/**
 * Observes both view=".." and data="..." expressions to control rendering of view
 */
class RerenderCheck extends RenderExpression {

	flag: boolean = false;
	viewExpression: ViewExpression;

	constructor( viewExpression :ViewExpression, expression:string ) {
		super( viewExpression.context, expression )
		this.viewExpression = viewExpression;
	}

	update() {
		if( this.flag ) this.viewExpression.renderView();
		this.flag = false;
	}

}
